import { Flex, Form, Modal, Select, Input } from 'antd';
import { UserPlus } from 'lucide-react';
import { Store } from 'antd/es/form/interface';
import { useTranslation } from 'react-i18next';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { AddIcon } from '@/components/Icons';
import { Heading } from '@/components/Heading';
import { toast } from '@/hooks/use-toast';
import { useFetchWorkspaceRoles, useInviteToWorkspace } from '@/hooksApi';
import { attachCountryCodeToPhone } from '@/utils/helpers/phoneNumberHelpers';

/**
 * The create-order form's field class, verbatim
 * (`OrderForm/CustomerDetailsCard`). Shared by both workspace modals so they
 * cannot drift apart from it or from each other.
 */
const FIELD = 'border border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md w-full';

interface Props {
  workspaceUid: string;
  workspaceName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * BOXY-44 — adding somebody to a workspace.
 *
 * 🔴 THIS IS `TeamMemberModal` + `TeamMemberForm`, POINTED AT ANOTHER
 * WORKSPACE. Owner, 2026-09-16, rejecting the second attempt: "ui bugs and ui
 * tottaly rejected".
 *
 * The first attempt copied `pages/settings/team-members/create.tsx` — which is
 * a PAGE, with a breadcrumb — and put it inside a shadcn `Dialog`. Both halves
 * were wrong. The dashboard has 33 AntD `Modal`s and 3 shadcn `Dialog`s (two of
 * them mine), and the "invite user in members view" the owner kept pointing at
 * is `SettingsCards/TeamMemberSection/TeamMemberModal`. Copying the page into
 * the wrong shell is why every single detail came out subtly off — grey ground
 * instead of white, a yellow focus ring instead of AntD's inline red message,
 * labels above the fields instead of placeholders inside them, and a country
 * list that would not scroll because Radix locks the wheel inside its overlay.
 *
 * ⭐ So the chrome, the grid, the field classes, the validation and the footer
 * buttons are taken from `TeamMemberForm` rather than re-derived. Adding a
 * colleague should not feel like a different product depending on which page
 * the merchant started from, and the way to guarantee that is to use the same
 * components, not to match them by eye.
 *
 * Two deliberate differences, both because the tenant is another workspace:
 *   - roles come from the TARGET workspace, not the active one;
 *   - no "add a custom role" in the dropdown — roles are created inside the
 *     workspace that owns them, and there is no endpoint to mint one in a
 *     workspace you are not currently in.
 *
 * ⚠️ TWO OUTCOMES, AND THE SERVER SAYS WHICH. A phone with no Boxy account is
 * created as a member immediately — there is nobody to ask for consent, and the
 * merchant is creating a staff account exactly as Team Members does. A phone
 * that already has an account gets a pending invitation, because attaching an
 * existing identity to someone else's workspace is theirs to agree to. The
 * response always carries both keys, so this never has to guess from a missing
 * field, and the toast says which one happened.
 */
const InviteMemberDialog = ({ workspaceUid, workspaceName, open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Only while the dialog is open: one request per workspace actually staffed,
  // not one per workspace the merchant owns.
  const { data: rolesData } = useFetchWorkspaceRoles(workspaceUid, open);
  const invite = useInviteToWorkspace(workspaceUid);

  const roles = rolesData?.object ?? [];

  const close = () => {
    form.resetFields();
    onOpenChange(false);
  };

  const onFinish = (values: Store) => {
    invite.mutate(
      {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: attachCountryCodeToPhone(values.phone, values.countryCode) || '',
        role_uids: (values.role || []) as string[],
      },
      {
        onSuccess: (data) => {
          // Which of the two things happened is the server's answer, not a
          // guess from an absent field — both keys are always present.
          toast({
            title: data?.object?.member
              ? String(t('workspace.member_added', { workspace: workspaceName ?? '' }))
              : String(t('workspace.invite_sent')),
          });
          close();
        },
        onError: (error: any) => {
          /*
           * The server's own sentence. Its refusals are the actionable kind —
           * "this phone is already a member of the workspace", "that role does
           * not belong to this workspace" — and `error.message` alone would
           * read "Request failed with status code 400", which tells the
           * merchant nothing at all.
           */
          const message = error?.response?.data?.message || error?.message;
          toast({
            title: String(t('error_title')),
            description: message || String(t('operation_failed')),
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        /* 🔴 THE WORKSPACE NAME IS NOT PART OF THE TITLE. Appended with an
           em dash it made a one-line heading as long as the workspace is
           named — "INVITE A MEMBER — BOXY44 FIXTURE A — SYNTHETIC PRINCIPAL,
           3 MEMBERS" ran under the close button (QA V1). A title is a fixed
           string; the thing it is about goes on its own line and truncates. */
        <div
          style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '1rem' }}
          className='pe-8'
        >
          <Heading
            title={String(t('workspace.invite_title'))}
            upperCase
            fontSize='text-lg'
            fontWeight='font-semibold'
          />
          {workspaceName ? (
            <div className='truncate text-xs font-normal text-sd-muted-foreground'>
              <bdi dir='auto'>{workspaceName}</bdi>
            </div>
          ) : null}
        </div>
      }
      width={760}
      centered
      footer={null}
      open={open}
      onOk={close}
      onCancel={close}
      destroyOnClose
    >
      <Form form={form} name='workspace-invite-form' className='workspace-form' onFinish={onFinish}>
        {/* ⭐⭐⭐ THIS IS `OrderForm/CustomerDetailsCard`'s RHYTHM, MEASURED AND
            COPIED, NOT APPROXIMATED. Owner, 2026-09-16: "color, gaps, size,
            everything is rejected … implement it neat, clean and pixel perfect
            like other forms that we have. You check the create order form,
            everything is clear — gaps, margin, sizes."

            The create-order form spaces itself EXPLICITLY: every field sits in
            a `mb-3` wrapper with `Form.Item className='mb-0'`, so the rhythm is
            one number (12px) instead of AntD's default 24px bottom margin
            fighting whatever the row does. My version used `Row gutter={[16,0]}`
            — zero vertical gutter — plus default Form.Item margins, and the
            phone row had no Form.Item at all, which is why "Roles" sat jammed
            under the phone field while everything else had a wide gap.

            Fields: `h-[40px] rounded-md border-sd-input bg-sd-input-bg`, the
            literal class string from that form. `PhoneNumberInput` already
            carries it — it was built for this form — so the three controls
            match by construction rather than by my eye. */}
        <div className='mb-4'>
          <div className='mb-3 flex items-center gap-2'>
            <UserPlus className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
            <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
              {String(t('member_details'))}
            </span>
          </div>

          <div className='mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <Form.Item
              name='first_name'
              className='mb-0'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Input placeholder={String(t('first_name'))} className={FIELD} />
            </Form.Item>
            <Form.Item
              name='last_name'
              className='mb-0'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Input placeholder={String(t('last_name'))} className={FIELD} />
            </Form.Item>
          </div>

          <div className='mb-3 flex gap-2' style={{ direction: 'ltr' }}>
            <PhoneNumberInput
              form={form}
              countryCodeName={'countryCode'}
              phoneNumberName={'phone'}
              phoneNumberPlaceholder={String(t('primary_phone'))}
              required
            />
          </div>

          <div className='mb-3'>
            <Form.Item
              name='role'
              className='mb-0'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Select
                mode='multiple'
                className={`custom-select-placeholder ${FIELD}`}
                /* Chips collapse into "+N" rather than wrapping onto a second
                   row, so the control keeps the same 40px as the two fields
                   above it. `custom-multi-select-item` is deliberately NOT
                   used: it carries a 52px min-height shared with
                   TeamMemberForm and two TriggerSelectors. */
                maxTagCount='responsive'
                placeholder={String(t('select_roles'))}
              >
                {/* ⚠️ AR SHOWS THE ENGLISH NAME. Team Members renders
                    `role.description` in Arabic, but
                    `GET workspaces/{uid}/roles` returns only
                    `uid`/`name`/`is_default` — the field is not there to
                    render. Faking it from `name` would be a guess at a
                    translation; the fix is additive on the resource, and it is
                    filed rather than invented here. */}
                {roles.map((role) => (
                  <Select.Option key={role.uid} value={role.uid}>
                    {role.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        <Flex className='mt-8' justify='end' gap={16}>
          <button
            type='button'
            onClick={close}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
          >
            {t('cancel')}
          </button>
          <button
            type='submit'
            disabled={invite.isLoading}
            className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
          >
            <AddIcon width={20} height={20} />
            {invite.isLoading ? t('loading') : t('workspace.invite_member')}
          </button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default InviteMemberDialog;
